// Pure helpers for branching connections off nodes other than yourself.
//
// A connection you own points at a "person" — a real Aline user (recipient_uid)
// or a name-only contact (no uid). `branch_parent_uid` optionally attaches that
// person under another node in your network; `branch_mode` says how:
//   "sub"    → hangs off the parent only (extended network, one hop further out)
//   "direct" → still your degree-1 contact, with provenance ("met via X")

/** Stable key identifying the person a connection is about. */
export function personKey(conn) {
  return conn.recipient_uid || conn.id;
}

function effectiveParentKey(conn, byKey) {
  if (
    conn.branch_mode === "sub" &&
    conn.branch_parent_uid &&
    byKey.has(conn.branch_parent_uid)
  ) {
    return conn.branch_parent_uid;
  }
  return null;
}

/**
 * Build a nested tree from a flat list of your connection docs.
 * Only "sub"-mode branches whose parent is also in your list nest; everything
 * else (no parent, "direct" mode, or deep/absent parent) stays a root.
 * Returns roots: [{ connection, children, depth }].
 */
export function buildConnectionTree(connections) {
  const list = connections || [];
  const byKey = new Map();
  list.forEach((c) => byKey.set(personKey(c), c));

  // Resolve each connection's effective parent, breaking any cycles.
  const parentOf = new Map(); // conn.id -> parent conn | null
  list.forEach((c) => {
    const pk = effectiveParentKey(c, byKey);
    let parent = pk ? byKey.get(pk) : null;
    if (parent) {
      const seen = new Set([c.id]);
      let cur = parent;
      let guard = 0;
      while (cur && guard++ < 10000) {
        if (seen.has(cur.id)) {
          parent = null; // cycle — treat c as a root
          break;
        }
        seen.add(cur.id);
        const ck = effectiveParentKey(cur, byKey);
        cur = ck ? byKey.get(ck) : null;
      }
    }
    parentOf.set(c.id, parent);
  });

  const nodeById = new Map();
  list.forEach((c) => nodeById.set(c.id, { connection: c, children: [], depth: 0 }));

  const roots = [];
  list.forEach((c) => {
    const parent = parentOf.get(c.id);
    if (parent) nodeById.get(parent.id).children.push(nodeById.get(c.id));
    else roots.push(nodeById.get(c.id));
  });

  const assignDepth = (node, d) => {
    node.depth = d;
    node.children.forEach((ch) => assignDepth(ch, d + 1));
  };
  roots.forEach((r) => assignDepth(r, 0));

  return roots;
}

/**
 * Overlay your manual branches onto an auto-built graph (nodes/edges).
 * Non-mutating — returns fresh { nodes, edges }.
 */
export function applyManualBranches(nodes, edges, myConnections, currentUid) {
  const branchDocs = (myConnections || []).filter(
    (c) =>
      c.initiator_uid === currentUid &&
      c.branch_parent_uid &&
      (c.branch_mode === "sub" || c.branch_mode === "direct")
  );
  if (branchDocs.length === 0) return { nodes, edges };

  const nodesOut = nodes.map((n) => ({ ...n }));
  const edgesOut = edges.map((e) => ({ ...e }));
  const nodeById = new Map(nodesOut.map((n) => [n.id, n]));

  const edgeEnd = (v) => (typeof v === "object" && v ? v.id : v);

  branchDocs.forEach((b) => {
    const recipientId = b.recipient_uid || b.id;

    // Resolve parent node; fall back to "you" if it isn't in the graph.
    let parentNode = nodeById.get(b.branch_parent_uid) || nodeById.get(currentUid);
    if (!parentNode) return;
    const parentUid = parentNode.id;

    // Ensure the recipient node exists.
    let recNode = nodeById.get(recipientId);
    if (!recNode) {
      recNode = {
        id: recipientId,
        degree: 1,
        profile: { id: recipientId, full_name: b.recipient_name || "Unknown" },
        relationshipType: b.relationship_type,
        connectionStrength: b.connection_strength,
        parentUid: currentUid,
      };
      nodesOut.push(recNode);
      nodeById.set(recipientId, recNode);
    }
    recNode.manualBranch = true;

    if (b.branch_mode === "sub") {
      recNode.degree = parentNode.degree + 1;
      recNode.parentUid = parentUid;
      // Drop any existing primary edge into the recipient (e.g. you -> recipient).
      for (let i = edgesOut.length - 1; i >= 0; i--) {
        if (edgeEnd(edgesOut[i].target) === recipientId && !edgesOut[i].secondary) {
          edgesOut.splice(i, 1);
        }
      }
      edgesOut.push({
        source: parentUid,
        target: recipientId,
        degree: recNode.degree,
        connectionStrength: b.connection_strength,
        manualBranch: true,
      });
    } else {
      // direct: still your degree-1 contact, but on the map it attaches to the
      // connection you met them through — a solid link to the parent, never a
      // line through you. (The sidebar list still shows them top-level "via X".)
      // Flagged so the graph renders them as a filled, colored circle — a
      // contact of yours — rather than a hollow extended-network node.
      recNode.degree = 1;
      recNode.directBranch = true;
      recNode.parentUid = parentUid;
      // Drop any existing primary edge into the recipient (e.g. you -> recipient).
      for (let i = edgesOut.length - 1; i >= 0; i--) {
        if (edgeEnd(edgesOut[i].target) === recipientId && !edgesOut[i].secondary) {
          edgesOut.splice(i, 1);
        }
      }
      edgesOut.push({
        source: parentUid,
        target: recipientId,
        degree: 1,
        connectionStrength: b.connection_strength,
        manualBranch: true,
      });
    }
  });

  return { nodes: nodesOut, edges: edgesOut };
}
